// @ts-nocheck

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

// Actions
import AppActions from 'app/shared/flux/actions/AppActions';
import ContactActions from 'app/shared/flux/actions/ContactActions';
import ConversationActions from 'app/shared/flux/actions/ConversationActions';
import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import NotificationActions from 'app/shared/flux/actions/NotificationActions';
import UserActions from 'app/shared/flux/actions/UserActions';
import UserSearchActions from 'app/shared/flux/actions/UserSearchActions';
import FetchListingActions from 'app/shared/flux/actions/FetchListingActions';

// Components
import ChatButton from 'app/shared/modules/hdp/ChatButton';
import ContactForm from 'app/shared/modules/hdp/form/Contact/ContactForm';
import ContactListedBy from 'app/shared/modules/hdp/form/Contact/ContactListedBy';
import ContactOverlay from 'app/shared/modules/hdp/form/Contact/ContactOverlay';
import Disclaimer from 'app/shared/modules/hdp/form/Disclaimer';
import SatelliteMsgStatus from 'app/shared/modules/satellite/SatelliteMsgStatus';
import LandlordInfo from 'app/shared/modules/hdp/form/Contact/LandlordInfo';
import WaitlistSuccessPopup from 'app/shared/modules/hdp/form/WaitlistSuccessPopup';

// Misc / Utils
import {
  contactStyles,
  containerStyles,
  contentStyles,
  contentWrapperStyles,
  disclaimerStyles,
} from './Contact.styles';
import { trackForRentContact } from 'app/client/tracking';
import contactUtils from 'app/shared/utils/contactUtils';
import loginUtils from 'app/shared/utils/loginUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import queryUtils from 'app/shared/utils/queryUtils';
import routeUtils from 'app/shared/utils/routeUtils';
import {
  emitTrackInlineContactSubmitEvent,
  emitTrackModalContactFormViewEvent,
  emitTrackModalContactSubmitEvent,
} from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import { getListingType, getTrackingFunction } from 'app/shared/utils/clickstreamUtils';
import { noop } from 'lodash';
import { useModal } from 'app/shared/contexts/ModalContext/useModal';
import type { RootReduxState } from 'app/shared/flux/reducers';

const Contact = (props) => {
  const dispatch = useDispatch();
  const store = useStore(); // Get store to read latest state directly

  // Get values from Redux store
  const clientLoadedFromStore = useSelector((state: RootReduxState) => state.app.clientLoaded);
  const conversationsFromStore = useSelector((state: RootReduxState) => state.user.conversations);
  const countryCodeFromStore = useSelector((state: RootReduxState) => state.geolocation.area.countryCode);
  const currentListingFromStore = useSelector((state: RootReduxState) => state.currentListingDetails.currentListing);
  const hasShownPostContactSavedSearchFromStore = useSelector(
    (state: RootReduxState) => state.app.hasShownPostContactSavedSearch,
  );
  const isLoggedInFromStore = useSelector((state: RootReduxState) => state.user.loggedIn);
  const isMobileFromStore = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');
  const similarListingsFromStore = useSelector((state: RootReduxState) => state.fetchListing.similarListings) || [];
  const userFromStore = useSelector((state: RootReduxState) => state.user);
  const userInquiryDataCacheFromStore = useSelector((state: RootReduxState) => state.user.inquiryDataCache);

  // Get pathname and search params from location (withRouter provides these)
  const pathnameFromLocation = props.location?.pathname;
  const shouldUseAreaBoundaryFromLocation = props.location
    ? queryUtils.parse(props.location.search).border !== false
    : true;

  const {
    className,
    clientLoaded = clientLoadedFromStore,
    closePopup,
    onClose,
    conversations = conversationsFromStore,
    countryCode = countryCodeFromStore,
    currentListing = currentListingFromStore,
    customForm,
    customTracking,
    hasShownPostContactSavedSearch = hasShownPostContactSavedSearchFromStore,
    hdpV3Form,
    isCachedDataPending,
    isLoggedIn = isLoggedInFromStore,
    isMobile = isMobileFromStore,
    isPopup = false,
    listing = {
      contact: {},
      userItemTypes: [],
    },
    onHidePopup = noop,
    pathname = pathnameFromLocation,
    shouldUseAreaBoundary = shouldUseAreaBoundaryFromLocation,
    similarListings = similarListingsFromStore,
    unitDetails = {},
    user = userFromStore,
    userInquiryDataCache = userInquiryDataCacheFromStore,
  } = props;

  const { openModal } = useModal();

  const { userOnWaitlist } = listing;

  const [state, setState] = useState({
    activeModal: null,
    contactSubmitted: false,
    emailErrorMessage: '',
    errorMessage: '',
    isLoading: false,
    isSuccessPopupVisible: false,
    phoneErrorMessage: '',
    previousInquiryMessagePopup: includes(listing.userItemTypes, 'inquiry') || userOnWaitlist,
  });

  const [shouldDisplayPostContactSavedSearch, setShouldDisplayPostContactSavedSearch] = useState(false);

  useEffect(() => {
    const { previousInquiryMessagePopup } = state;
    const useMapBounds = routeUtils.isAreaUrl(pathname) || routeUtils.isPadOrBuildingUrl(pathname);

    const nextInquiryMessagePopup = includes(listing.userItemTypes, 'inquiry') || userOnWaitlist;
    if (previousInquiryMessagePopup !== nextInquiryMessagePopup) {
      setState((prev) => ({
        ...prev,
        previousInquiryMessagePopup: nextInquiryMessagePopup,
      }));
    }

    if (userInquiryDataCache.isPending) {
      dispatch(UserActions.inquiry.getMessageCache());
    }

    let isMounted = true;
    dispatch(UserSearchActions.search.saved.displaySearchPromptInquiry({ shouldUseAreaBoundary, useMapBounds }))
      .then((res) => {
        if (isMounted) {
          const { data } = res;
          setShouldDisplayPostContactSavedSearch(Boolean(data));
        }
        return res;
      })
      .catch((error) => {
        if (isMounted) {
          dispatch(
            ErrorActions.errorHandler({
              error,
              errorLocation: 'component.contact.componentDidMount',
              errorClass: 'userSearchActions',
            }),
          );
        }
      });

    if (isPopup) {
      emitTrackModalContactFormViewEvent({
        listing: currentListing,
        triggerLocation: 'home_details',
        triggerObject: 'home_details_component|modal_contact_form',
      });
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetActiveModal = useCallback((modal = null) => {
    setState((prev) => ({
      ...prev,
      activeModal: modal,
    }));
  }, []);

  const handlePhoneClick = useCallback(() => {
    const listingType = getListingType(currentListing);
    const leadType = 'phone';

    if (isPopup) {
      getTrackingFunction({ type: listingType, leadType })?.({
        listing: currentListing,
        triggerObject: 'home_details_component|modal_contact_form',
        triggerSource: 'button_to_complete_contact_form',
      });
    } else {
      getTrackingFunction({ type: listingType, leadType })?.({
        listing: currentListing,
        triggerObject: 'home_details_component|inline_form',
        triggerSource: 'button_to_complete_contact_form',
      });
    }
  }, [currentListing, isPopup]);

  const handleTrackSubmitClick = useCallback(
    (rentalSubmitId) => {
      const listingType = getListingType(currentListing);

      if (customTracking) {
        customTracking();
      } else if (isPopup && !customTracking) {
        emitTrackModalContactSubmitEvent({
          listing: currentListing,
          rentalSubmitId,
          triggerLocation: 'home_details',
          triggerObject: 'home_details_component|modal_contact_form',
          triggerSource: 'button_to_complete_contact_form',
        });
      } else {
        emitTrackInlineContactSubmitEvent({
          listing: currentListing,
          rentalSubmitId,
          triggerLocation: 'home_details',
        });
      }
      const leadType = 'submit';

      if (isPopup) {
        getTrackingFunction({ type: listingType, leadType })?.({
          listing: currentListing,
          triggerObject: 'home_details_component|modal_contact_form',
          triggerSource: 'button_to_complete_contact_form',
        });
      } else {
        getTrackingFunction({ type: listingType, leadType })?.({
          listing: currentListing,
          triggerObject: 'home_details_component|inline_form',
          triggerSource: 'button_to_complete_contact_form',
        });
      }
    },
    [currentListing, customTracking, isPopup],
  );

  const handleFormSubmitFailure = useCallback((response = {}) => {
    setState((prev) => ({
      ...prev,
      isLoading: false,
      errorMessage: response.message || 'Failed to send inquiry.',
    }));
    logger.error(response, 'Form submit failure');
  }, []);

  const getInquiryParams = useCallback(
    (inputValues) => {
      const { trusted, aliasEncoded } = listing;
      const { demoted } = listing.details;
      const listingPaid = Boolean(trusted);
      const listingDemoted = Boolean(demoted);

      return {
        alias: aliasEncoded,
        displayName: listing.displayName,
        address: listing.address.street,
        baths: inputValues.numBaths,
        beds: inputValues.numBeds,
        email: inputValues.userEmail,
        phone: numberUtils.formatPhoneNumber(inputValues.userPhoneNumber),
        name: inputValues.userName,
        text: contactUtils.getInquiryText({
          userMessage: inputValues.userMessage,
          lowIncomeMessage: inputValues.lowIncomeMessage,
          listing,
        }),
        textToCache: inputValues.userMessage,
        emailUser: true,
        defaultMessage: listing.contact.defaultInquiryMessage,
        paid: listingPaid,
        demoted: listingDemoted,
        modelId: unitDetails.modelId ? unitDetails.modelId : null,
        requestToApply: inputValues.requestToApply,
      };
    },
    [listing, unitDetails],
  );

  const validateUserInfo = useCallback(
    async (inputValues) => {
      const { aliasEncoded: listingAlias } = listing;
      const email = inputValues.userEmail;
      const phone = inputValues.userPhoneNumber;
      const params = { listingAlias, email, phone };

      // Reset state before proceeding
      setState((prev) => ({
        ...prev,
        emailErrorMessage: '',
        phoneErrorMessage: '',
        isLoading: true,
      }));

      // Call validation endpoint
      try {
        const res = await dispatch(UserActions.inquiry.validateUserContactInfo(params));
        const data = res.data;
        const emailData = data.email;
        const phoneData = data.phone;

        // If errors are detected, let the UI know
        if (!emailData.valid) {
          setState((prev) => ({
            ...prev,
            emailErrorMessage: 'Invalid email address',
            isLoading: false,
          }));
        }

        if (!phoneData.valid) {
          setState((prev) => ({
            ...prev,
            phoneErrorMessage: 'Invalid phone number',
            isLoading: false,
          }));
        }

        return { isValid: emailData.valid && phoneData.valid };
      } catch (error) {
        logger.error(error, 'Phone and/or email validation error');
        setState((prev) => ({
          ...prev,
          errorMessage: 'We could not validate your contact information. Please try again.',
          isLoading: false,
        }));
        return { isValid: false };
      }
    },
    [dispatch, listing],
  );

  /**
   * Continues submit contact inquiry flow after user closes Renter Profile popup modal
   */
  const continueContactInquiryFlow = useCallback(async () => {
    const { trusted, aliasEncoded } = listing;
    const { inquiryParams, rentalSubmitId, wasLoggedInAtStart } = contactUtils.getDelayedContactInquiry();

    // IMPORTANT: Read userExists from Redux store directly to get the latest value
    // The useSelector hook won't update until the component re-renders,
    // but we need the value immediately after it's set in handleSubmitForm
    const currentUserExists = store.getState().app.userExists;

    try {
      await dispatch(
        ContactActions.submitContact({
          listing,
          inquiryParams,
          rentalSubmitId,
        }),
      );

      trackForRentContact(listing);

      // Close the current modal first
      if (onClose) {
        onClose();
      } else if (closePopup) {
        closePopup();
      } else {
        onHidePopup();
      }
      // Update internal state
      setState((prev) => ({
        ...prev,
        isLoading: false,
        isSuccessPopupVisible: true,
        contactSubmitted: true,
      }));
      // Get listings that have not been inquired/contacted by the user
      const availableSimilarListings = await dispatch(
        FetchListingActions.fetchSimilarListingsFromApi({
          maloneLotIdEncoded: listing.maloneLotIdEncoded,
          unit: listing.unit,
          listingTypes: listing.listingType,
          limit: 7,
          excludeInquiredListings: true,
        }),
      );

      const hasAvailableSimilarListings = availableSimilarListings && availableSimilarListings.length > 0;

      // Chain additional modals after successful contact submission
      // Use wasLoggedInAtStart (captured state) instead of isLoggedIn (current Redux state)
      // to prevent showing PostContactSavedSearch to users who were logged out initially
      // but became logged in via freepass during the contact flow
      if (shouldDisplayPostContactSavedSearch && wasLoggedInAtStart && !hasShownPostContactSavedSearch) {
        openModal({
          type: 'postContactSavedSearch',
          onClose: () => {
            // After saved search modal closes, show contact success if available
            if (hasAvailableSimilarListings) {
              openModal({
                type: 'contactSuccess',
                // IMPORTANT: Must pass listing prop - ContactSimilarListingsV2 needs it to avoid undefined errors (HPWEB-6566)
                props: { listing, similarListings: availableSimilarListings, userExists: currentUserExists },
                onClose: () => noop,
              });
            }
          },
        });
      } else if (listing.waitlisted && hasAvailableSimilarListings) {
        openModal({
          type: 'waitlistSuccess',
          props: { similarListings: availableSimilarListings },
          onClose: noop,
        });
      } else if (hasAvailableSimilarListings) {
        openModal({
          type: 'contactSuccess',
          // IMPORTANT: Must pass listing prop - ContactSimilarListingsV2 needs it to avoid undefined errors (HPWEB-6566)
          props: { listing, similarListings: availableSimilarListings, userExists: currentUserExists },
          onClose: noop,
        });
      }

      if (!trusted) {
        dispatch(ConversationActions.optimisticSetSentMessage(aliasEncoded));
      }
    } catch (error) {
      dispatch(
        ErrorActions.errorHandler({
          error,
          errorLocation: 'component.contact.continueContactInquiryFlow',
          errorClass: 'contactActions',
        }),
      );

      handleFormSubmitFailure();
    }
  }, [
    dispatch,
    listing,
    onClose,
    closePopup,
    onHidePopup,
    shouldDisplayPostContactSavedSearch,
    openModal,
    handleFormSubmitFailure,
    hasShownPostContactSavedSearch,
    store,
  ]);

  const handleSubmitForm = useCallback(
    async (inputValues) => {
      // Capture login state BEFORE any login operations
      // This ensures we use the original login state for modal flow decisions,
      // not the potentially changed state after freepass login
      const wasLoggedInAtStart = isLoggedIn;

      /**
       * DISABLE CONTACT INFO VALIDATION
       * Turns off contact info validation when developing. This should speed
       * things up when devs need to work on CTA related logic.
       *
       * __DEV__ flag should ensure that this never gets enabled in production environments.
       */
      const DISABLE_VALIDATION_FOR_CONTACT_TESTING = true;
      const { isValid } =
        DISABLE_VALIDATION_FOR_CONTACT_TESTING && __DEV__ ? { isValid: true } : await validateUserInfo(inputValues);

      if (isValid) {
        try {
          const rentalSubmitId = uuidv4();

          handleTrackSubmitClick(rentalSubmitId);
          setState((prev) => ({
            ...prev,
            isLoading: true,
            emailErrorMessage: '',
            errorMessage: '',
          }));

          await dispatch(AppActions.setAppStoreBool('userExists', false));
          const userDetails = await dispatch(ContactActions.loginUser(user.loggedIn, inputValues.userEmail));

          if (!userDetails) {
            handleFormSubmitFailure();
            return;
          }

          // Check for specific status codes BEFORE checking for generic errors
          // This is important because USER_ALREADY_EXISTS returns success: false
          // but is a valid state that we need to handle specially
          if (userDetails.status === 'ACCOUNT_DEACTIVATED') {
            setState((prev) => ({
              ...prev,
              isLoading: false,
              emailErrorMessage:
                'This email is associated with an account marked for deletion. Please use another email address.',
            }));

            return;
          }

          if (userDetails.status === 'USER_ALREADY_EXISTS') {
            dispatch(AppActions.setAppStoreBool('userExists', true));
          } else if (userDetails.error) {
            // Check for generic errors AFTER specific status handling
            handleFormSubmitFailure(userDetails);
            return;
          } else if (
            userDetails.success &&
            userDetails?.data?.passwordStatus?.toLowerCase() === 'freepass' &&
            loginUtils.isCanadianUser(countryCode, inputValues.userEmail)
          ) {
            // freepassed user from canada
            dispatch(NotificationActions.email.updateEmailOptIn(true, 'inquiry'));
          }

          // Store contact inquiry details for sending after user closes rental profile form.
          // Include captured login state to ensure correct modal flow decisions
          contactUtils.setDelayedContactInquiry({
            listing,
            inquiryParams: getInquiryParams(inputValues),
            rentalSubmitId,
            wasLoggedInAtStart,
          });

          // Open renter profile modal first, then continue the contact flow
          openModal({
            type: 'postContactRenterProfile',
            onClose: continueContactInquiryFlow,
          });
        } catch (error) {
          dispatch(
            ErrorActions.errorHandler({
              error,
              errorLocation: 'component.contact.handleSubmitForm',
              errorClass: 'contactActions',
            }),
          );

          handleFormSubmitFailure();
        }
      }
    },
    [
      isLoggedIn,
      validateUserInfo,
      handleTrackSubmitClick,
      dispatch,
      user.loggedIn,
      countryCode,
      listing,
      getInquiryParams,
      openModal,
      continueContactInquiryFlow,
      handleFormSubmitFailure,
    ],
  );

  const handleOverlay = useCallback(() => {
    // This function is referenced but not defined in the original component
    // Keeping empty for compatibility
  }, []);

  const renderModal = useCallback(() => {
    const { activeModal } = state;

    if (!activeModal) {
      return null;
    }

    return (
      <div className={contactStyles.modal}>
        {activeModal === 'landlordInfo' && (
          <LandlordInfo isPopup={isPopup} onClickPhoneContact={handlePhoneClick} onCloseModal={handleSetActiveModal} />
        )}
      </div>
    );
  }, [state, isPopup, handlePhoneClick, handleSetActiveModal]);

  const {
    contactSubmitted,
    emailErrorMessage,
    errorMessage,
    isLoading,
    isSuccessPopupVisible,
    phoneErrorMessage,
    previousInquiryMessagePopup,
  } = state;

  const {
    address,
    aliasEncoded,
    maloneLotIdEncoded,
    trusted,
    contact,
    listedBy,
    details,
    isApartmentBldg,
    waitlisted,
  } = listing;

  const css = cx(className, !hdpV3Form ? containerStyles({ isPopup }) : undefined);
  const CustomForm = customForm || null;
  if (isSuccessPopupVisible && !previousInquiryMessagePopup && waitlisted) {
    return (
      <div className={css}>
        <WaitlistSuccessPopup
          onHide={() => {
            if (isPopup) {
              onHidePopup();
            }

            setState((prev) => ({
              ...prev,
              isSuccessPopupVisible: false,
            }));
          }}
          similarListings={similarListings}
        />
      </div>
    );
  }

  if (isEmpty(contact)) {
    return null;
  }

  const hasConversation = conversations[aliasEncoded];
  const satelliteEnabled = hasConversation ? conversations[aliasEncoded].satelliteEnabled : false;

  return (
    <div className={css} data-testid="contact-box-0">
      <div
        className={
          !hdpV3Form
            ? contentWrapperStyles({ isInline: !isPopup })
            : cx(
                { 'HdpForm-Contact-content-wrapper': !hdpV3Form },
                {
                  'HdpForm-Contact-content-wrapper-inline': !isPopup && !hdpV3Form,
                },
              )
        }
        data-testid="contact-box-1"
      >
        {contact.displayContactBox ? (
          <div
            className={
              !hdpV3Form
                ? contentStyles({ isPopup })
                : cx(
                    { 'HdpForm-Contact-content': !hdpV3Form },
                    {
                      'HdpForm-Contact-content-popup': isPopup,
                    },
                  )
            }
            data-testid="contact-box-2"
          >
            <ContactOverlay
              overlayOffset={hdpV3Form ? '61px' : null}
              contactSubmitted={contactSubmitted}
              hasConversation={hasConversation}
              previousInquiryMessagePopup={previousInquiryMessagePopup}
              onHideOverlay={handleOverlay}
              satelliteEnabled={satelliteEnabled}
              listing={listing}
              shouldLockFocus={false}
            />
            {!hdpV3Form && (
              <ContactListedBy
                address={address}
                details={details}
                isApartmentBldg={isApartmentBldg}
                isPopup={isPopup}
                listedBy={listedBy}
                maloneLotIdEncoded={maloneLotIdEncoded}
                onSetActiveModal={handleSetActiveModal}
                onClickPhoneContact={handlePhoneClick}
                trusted={trusted}
                waitlisted={waitlisted}
              />
            )}
            <ChatButton listing={listing} showCopy inContactBox />
            {hasConversation && (
              <SatelliteMsgStatus
                className={cx('SatelliteMsgStatus-inContactBox', {
                  'SatelliteMsgStatus-inContactBoxPopup': isPopup,
                })}
              />
            )}
            {(!hasConversation || !satelliteEnabled) && CustomForm && (
              <>
                <CustomForm
                  isCachedDataPending={isCachedDataPending ?? (userInquiryDataCache.isPending || false)}
                  clientLoaded={clientLoaded}
                  contactSubmitted={contactSubmitted}
                  emailErrorMessage={emailErrorMessage}
                  errorMessage={errorMessage}
                  isLoading={isLoading}
                  isMobile={isMobile}
                  isPopup={isPopup}
                  listing={listing}
                  onSubmitForm={handleSubmitForm}
                  phoneErrorMessage={phoneErrorMessage}
                  user={user}
                  userInquiryDataCache={userInquiryDataCache}
                />
              </>
            )}

            {(!hasConversation || !satelliteEnabled) && !CustomForm && (
              <ContactForm
                isCachedDataPending={isCachedDataPending ?? (userInquiryDataCache.isPending || false)}
                clientLoaded={clientLoaded}
                contactSubmitted={contactSubmitted}
                emailErrorMessage={emailErrorMessage}
                errorMessage={errorMessage}
                isLoading={isLoading}
                isMobile={isMobile}
                isPopup={isPopup}
                listing={listing}
                onSetActiveModal={handleSetActiveModal}
                onSubmitForm={handleSubmitForm}
                onClickPhoneContact={handlePhoneClick}
                phoneErrorMessage={phoneErrorMessage}
                user={user}
                userInquiryDataCache={userInquiryDataCache}
                renderModal={renderModal}
                {...unitDetails}
              />
            )}
            {renderModal()}
          </div>
        ) : (
          <div className={!hdpV3Form ? contentStyles({ isPopup: false }) : 'HdpForm-Contact-content'}>
            <ContactListedBy
              address={address}
              details={details}
              isApartmentBldg={isApartmentBldg}
              isPopup={isPopup}
              listedBy={listedBy}
              maloneLotIdEncoded={maloneLotIdEncoded}
              onSetActiveModal={handleSetActiveModal}
              trusted={trusted}
              waitlisted={waitlisted}
            />
            This property did not provide a contact email address.
            {renderModal()}
          </div>
        )}
      </div>
      <div
        className={
          !hdpV3Form
            ? disclaimerStyles({ isPopup })
            : cx('HdpForm-Contact-disclaimer', {
                'HdpForm-Contact-disclaimer-popup': isPopup,
              })
        }
      >
        <Disclaimer linkType={isPopup ? 'primary' : 'inverted'} />
      </div>
    </div>
  );
};

Contact.propTypes = {
  className: PropTypes.string,
  clientLoaded: PropTypes.bool,
  closePopup: PropTypes.func,
  onClose: PropTypes.func,
  conversations: PropTypes.object,
  countryCode: PropTypes.string,
  currentListing: PropTypes.object,
  customForm: PropTypes.elementType,
  customTracking: PropTypes.func,
  dispatch: PropTypes.func,
  hasShownPostContactSavedSearch: PropTypes.bool,
  hdpV3Form: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isMobile: PropTypes.bool,
  isPopup: PropTypes.bool,
  listing: PropTypes.object,
  location: PropTypes.object,
  onHidePopup: PropTypes.func,
  onShowSuccessModal: PropTypes.func,
  pathname: PropTypes.string,
  shouldUseAreaBoundary: PropTypes.bool,
  similarListings: PropTypes.array,
  unitDetails: PropTypes.object,
  user: PropTypes.object,
  userExists: PropTypes.bool,
  userInquiryDataCache: PropTypes.object,
};

export { Contact }; // destructured export for test
export default withRouter(Contact);
